import { useState, useEffect, useRef } from "react";

const questions = [
  {
    id: 1,
    question: "A Registered Representative (RR) is opening a new options account for a client. Under the \"Best Interest Standard,\" if the RR identifies two different vertical spread strategies that both meet the client's objective, the RR MUST:",
    options: ["Recommend the strategy that generates the highest commission for the firm.", "Recommend the strategy that the RR understands best personally.", "Recommend the strategy that is most advantageous to the client, considering costs and risks.", "Provide both options and let the client choose without a recommendation."],
    correct: 2,
    rationale: "Under the Client Focused Reforms (CFR), the \"Best Interest Standard\" requires that the client's interest be prioritized. If multiple suitable options exist, the one most beneficial to the client (lower cost/better risk-adjusted return) must be chosen."
  },
  {
    id: 2,
    question: "Which document must be provided to a retail client specifically BEFORE their first derivatives trade is executed?",
    options: ["The Corporate Resolution Form.", "The Standardized Risk Disclosure Statement.", "The Monthly Statement of Account.", "The Notice of Intention to Deliver."],
    correct: 1,
    rationale: "The CIRO-approved Risk Disclosure Statement is a mandatory pre-trade document that highlights the risks of leverage and volatility."
  },
  {
    id: 3,
    question: "An investor is \"Long\" a call option with a strike price of $60. The underlying stock is currently trading at $64. What is the intrinsic value of this option?",
    options: ["$0", "$4", "$60", "$64"],
    correct: 1,
    rationale: "Intrinsic value for a call is calculated as Stock Price − Strike Price ($64 − $60 = $4)."
  },
  {
    id: 4,
    question: "A client has $100,000 in net worth but needs $90,000 of that for a house down payment in three months. The RR determines the client has a high psychological \"Risk Tolerance\" but low \"Risk Capacity.\" How should the RR proceed?",
    options: ["Approve the account for aggressive naked option writing due to the high tolerance.", "Approve the account only if the client signs a waiver for the risk capacity.", "Decline the opening of a high-risk derivatives account as the client cannot afford the potential loss.", "Suggest the client use the $90,000 to hedge the house price using real estate futures."],
    correct: 2,
    rationale: "Suitability is based on the lower of capacity or tolerance. Since the client cannot afford the loss (low capacity), high-risk derivatives are unsuitable."
  },
  {
    id: 5,
    question: "Which of the following is a primary characteristic of an Over-the-Counter (OTC) derivative compared to a listed derivative?",
    options: ["Centralized clearing through the CDCC.", "High degree of contract standardization.", "Bilateral negotiation of contract terms.", "Public dissemination of all trade prices."],
    correct: 2,
    rationale: "OTC derivatives are private agreements tailored between two parties, whereas listed derivatives are standardized and exchange-traded."
  },
  {
    id: 6,
    question: "An investor enters into a \"Long\" position in one gold futures contract at $2,100. The initial margin is $10,000 and the maintenance margin is $8,000. If the price of gold drops to $2,070 (contract size = 100 oz), what is the required variation margin?",
    options: ["$2,000", "$3,000", "$8,000", "No margin is required yet."],
    correct: 1,
    rationale: "Loss = ($2,100 − $2,070) × 100 = $3,000. Current Equity = $10,000 − $3,000 = $7,000. Since $7,000 is below the $8,000 maintenance level, the client must refill the account back to the Initial level ($10,000 − $7,000 = $3,000)."
  },
  {
    id: 7,
    question: "\"Novation\" is the legal process by which:",
    options: ["A client transfers their account from one member firm to another.", "The clearinghouse becomes the buyer to every seller and the seller to every buyer.", "An out-of-the-money option is cancelled by the exchange.", "A stock split causes an adjustment to the strike price."],
    correct: 1,
    rationale: "Novation allows the CDCC to interpose itself in the middle of a trade, eliminating counterparty credit risk."
  },
  {
    id: 8,
    question: "An option's Delta is 0.50 and its Gamma is 0.04. If the underlying stock price rises by $1.00, what is the new estimated Delta?",
    options: ["0.46", "0.50", "0.54", "0.60"],
    correct: 2,
    rationale: "New Delta = Old Delta + (Gamma × Change in Stock Price). 0.50 + (0.04 × 1) = 0.54."
  },
  {
    id: 9,
    question: "A \"Trusted Contact Person\" (TCP) is primarily intended to help the RR in cases where:",
    options: ["The client wants to authorize a third party to trade on their behalf.", "The RR suspects the client is experiencing cognitive decline or financial exploitation.", "The client meeting takes place outside of regular business hours.", "The client meets the definition of an \"Institutional Investor.\""],
    correct: 1,
    rationale: "The TCP provides a safe point of contact for the RR if they have concerns about a vulnerable client's mental health or potential fraud."
  },
  {
    id: 10,
    question: "Which Greek measures an option's sensitivity to a 1% change in Implied Volatility?",
    options: ["Delta", "Theta", "Vega", "Rho"],
    correct: 2,
    rationale: "Vega measures the change in an option's price relative to changes in volatility."
  },
  {
    id: 11,
    question: "A \"Hedge Agreement\" is signed by a client to:",
    options: ["Waive their right to sue the firm for trading losses.", "Qualify for lower regulatory margin rates based on offsetting physical positions.", "Authorize the firm to move funds between a bank and a brokerage account.", "Certify that they are an Individual Institutional Investor."],
    correct: 1,
    rationale: "By signing a hedge agreement, the client represents they are hedging, which allows for reduced margin requirements."
  },
  {
    id: 12,
    question: "In the \"Mark-to-Market\" process for futures, what happens to the profit or loss at the end of each day?",
    options: ["It is recorded as \"unrealized\" until the position is closed.", "It is settled in cash between the clearinghouse and the member firms.", "It is only settled if the account falls below the maintenance margin.", "It is added to the \"Time Value\" of the contract."],
    correct: 1,
    rationale: "Futures are marked-to-market daily, meaning actual cash moves from the loser's account to the winner's account every night."
  },
  {
    id: 13,
    question: "A client holds 10 call options with a strike of $50. The company announces a 2-for-1 stock split. What is the new position?",
    options: ["10 contracts with a $25 strike.", "20 contracts with a $50 strike.", "20 contracts with a $25 strike.", "5 contracts with a $100 strike."],
    correct: 2,
    rationale: "In a 2-for-1 split, the number of contracts doubles (10 × 2 = 20) and the strike price is halved ($50 / 2 = $25)."
  },
  {
    id: 14,
    question: "Which of the following defines an \"Institutional Client\" (Entity) according to CIRO rules?",
    options: ["An individual with $1 million in financial assets.", "A non-individual entity with at least $25 million in net assets.", "Any client who has traded at least 1,000 derivative contracts.", "A corporation that holds at least $5 million in real estate."],
    correct: 1,
    rationale: "The standard threshold for an institutional entity is $25 million in net assets."
  },
  {
    id: 15,
    question: "If a market is in \"Contango,\" the futures price is:",
    options: ["Lower than the spot price.", "Higher than the spot price.", "Equal to the spot price.", "Moving in the opposite direction of the spot price."],
    correct: 1,
    rationale: "Contango is a normal market condition where futures prices are higher than spot prices due to the cost of carry (interest, storage)."
  },
  {
    id: 16,
    question: "An RR receives a large order from a client to buy 10,000 call options. Before entering the client's order, the RR buys 50 calls for their own personal account. This is a violation known as:",
    options: ["Wash Trading.", "Front-running.", "Spoofing.", "Marking the Close."],
    correct: 1,
    rationale: "Front-running is trading ahead of a client order to profit from the anticipated price move."
  },
  {
    id: 17,
    question: "The \"Time Value\" of an option is typically at its maximum when the option is:",
    options: ["Deep In-the-Money.", "At-the-Money.", "Deep Out-of-the-Money.", "Exercised early."],
    correct: 1,
    rationale: "At-the-money options have the most uncertainty regarding their eventual outcome, leading to the highest time value."
  },
  {
    id: 18,
    question: "Which order type becomes a MARKET order once a specific price level is touched?",
    options: ["Limit Order.", "Stop Order.", "Fill-or-Kill Order.", "Iceberg Order."],
    correct: 1,
    rationale: "A stop order is triggered by a price trade, at which point it becomes a market order."
  },
  {
    id: 19,
    question: "A client has a portfolio of $1,000,000 with a Beta of 1.5. They want to hedge against a market drop using index futures. If the index future is at 1,000 and the multiplier is $200, how many contracts should they sell?",
    options: ["5", "7.5 (Round to 8)", "10", "15"],
    correct: 1,
    rationale: "(Portfolio Value / [Futures Price × Multiplier]) × Beta. ($1,000,000 / [1,000 × $200]) × 1.5 = 5 × 1.5 = 7.5."
  },
  {
    id: 20,
    question: "A \"Bermudan Option\" is unique because it can be exercised:",
    options: ["Only at expiration.", "At any time before expiration.", "Only on a few specific, pre-determined dates.", "Only if the stock price hits a \"barrier\" level."],
    correct: 2,
    rationale: "Bermudan options are a hybrid between American and European styles, allowing exercise on specific dates."
  },
  {
    id: 21,
    question: "Under the \"Know Your Product\" (KYP) rule, an RR must:",
    options: ["Ensure the client has read the textbook on derivatives.", "Understand the structure, risks, and costs of a derivative before recommending it.", "Only recommend products that have been in the market for at least 5 years.", "Guarantee that the product will not lose money."],
    correct: 1,
    rationale: "KYP requires the advisor to perform due diligence on the products they sell."
  },
  {
    id: 22,
    question: "\"Theta\" measures the rate of change in an option's price due to:",
    options: ["Changes in the underlying stock price.", "The passage of time (time decay).", "Changes in the risk-free interest rate.", "Changes in market volatility."],
    correct: 1,
    rationale: "Theta is the \"time decay\" Greek, measuring daily premium erosion."
  },
  {
    id: 23,
    question: "Which of the following best describes \"Basis\" in the futures market?",
    options: ["The profit made on a trade.", "The difference between the spot price and the futures price.", "The initial margin required by the exchange.", "The interest rate charged on a margin loan."],
    correct: 1,
    rationale: "Basis = Spot Price − Futures Price."
  },
  {
    id: 24,
    question: "A \"Vertical Spread\" involves options that have:",
    options: ["Different underlying assets.", "The same strike price but different expiration months.", "The same expiration month but different strike prices.", "Different multipliers."],
    correct: 2,
    rationale: "Vertical spreads use the same expiration but different strike prices (vertical on an options chain)."
  },
  {
    id: 25,
    question: "If an investor \"Writes\" (Sells) a naked call, their maximum potential loss is:",
    options: ["The premium received.", "The strike price minus the premium.", "Unlimited.", "Limited to the initial margin deposit."],
    correct: 2,
    rationale: "A call writer is obligated to sell at the strike price. If the stock price rises infinitely, the loss is unlimited."
  },
  {
    id: 26,
    question: "Which organization acts as the central counterparty for the Montreal Exchange?",
    options: ["IIROC.", "CDCC.", "Bank of Canada.", "TSX Trust."],
    correct: 1,
    rationale: "The Canadian Derivatives Clearing Corporation (CDCC) clears and guarantees MX trades."
  },
  {
    id: 27,
    question: "A client undergoes a \"Material Change\" when they:",
    options: ["Buy a different brand of car.", "Experience a significant change in their net worth or income.", "Change their email address.", "Place a limit order instead of a market order."],
    correct: 1,
    rationale: "Material changes are events that impact a client's risk capacity or investment objectives."
  },
  {
    id: 28,
    question: "An \"American-Style\" option can be exercised:",
    options: ["Only on the expiration date.", "At any time up to and including the expiration date.", "Only during regular North American trading hours.", "Only if the client is a US citizen."],
    correct: 1,
    rationale: "American-style options allow for early exercise; European-style do not."
  },
  {
    id: 29,
    question: "What is the \"Multiplier\" for a standard equity option contract in Canada?",
    options: ["10", "50", "100", "1,000"],
    correct: 2,
    rationale: "One standard equity option contract controls 100 shares."
  },
  {
    id: 30,
    question: "A \"Short Straddle\" is a strategy used when an investor expects:",
    options: ["High market volatility.", "Low market volatility (a neutral market).", "A steady increase in the stock price.", "A sharp decline in the stock price."],
    correct: 1,
    rationale: "Short straddles profit from the passage of time and lack of price movement."
  },
  {
    id: 31,
    question: "\"Gamma\" is highest for options that are:",
    options: ["Deep in-the-money and far from expiration.", "Out-of-the-money and near expiration.", "At-the-money and near expiration.", "Deep in-the-money and near expiration."],
    correct: 2,
    rationale: "Gamma (the rate of change in Delta) peaks for at-the-money options as they approach expiry."
  },
  {
    id: 32,
    question: "The \"Put-Call Parity\" relationship applies to which type of options?",
    options: ["American-style equity options.", "European-style options with the same strike and expiry.", "Binary options on commodities.", "Only options that are out-of-the-money."],
    correct: 1,
    rationale: "Put-call parity is a theoretical relationship for European-style options."
  },
  {
    id: 33,
    question: "An investor is \"Long\" a future at $50.00. The price drops to $48.00. If the multiplier is 500, what is the dollar loss?",
    options: ["$200", "$500", "$1,000", "$2,000"],
    correct: 2,
    rationale: "($50 − $48) × 500 = 2 × 500 = $1,000."
  },
  {
    id: 34,
    question: "Which document authorizes the firm to liquidate a client's position without notice if there is a margin deficiency?",
    options: ["Relationship Disclosure Information.", "Margin Agreement.", "Trusted Contact Form.", "Settlement Confirmation."],
    correct: 1,
    rationale: "The margin agreement gives the dealer the legal right to protect themselves by closing risky, under-margined positions."
  },
  {
    id: 35,
    question: "A \"Synthetic Long Stock\" position can be created by:",
    options: ["Buying a Call and Selling a Put with the same strike.", "Selling a Call and Buying a Put with the same strike.", "Buying a Call and Buying a Put with the same strike.", "Selling a Call and Selling a Put with the same strike."],
    correct: 0,
    rationale: "Long Call + Short Put mimics the P&L of owning the stock."
  },
  {
    id: 36,
    question: "\"Open Interest\" represents the total number of:",
    options: ["Trades executed during the day.", "Shares of the underlying stock.", "Outstanding contracts that have not been settled or closed.", "Clients currently logged into the trading platform."],
    correct: 2,
    rationale: "Open interest measures the total number of \"live\" contracts in the market."
  },
  {
    id: 37,
    question: "When a stock pays a regular cash dividend, the strike price of the listed options is typically:",
    options: ["Adjusted downward by the dividend amount.", "Adjusted upward by the dividend amount.", "Left unchanged.", "Multiplied by the dividend yield."],
    correct: 2,
    rationale: "Regular cash dividends do NOT trigger an option adjustment; only special dividends or splits do."
  },
  {
    id: 38,
    question: "Which \"Greek\" would be most important for a trader concerned about rising interest rates?",
    options: ["Delta.", "Gamma.", "Vega.", "Rho."],
    correct: 3,
    rationale: "Rho measures sensitivity to changes in the risk-free interest rate."
  },
  {
    id: 39,
    question: "A \"Bull Call Spread\" is a:",
    options: ["Credit Strategy.", "Debit Strategy.", "Volatility Strategy.", "Time-decay Strategy."],
    correct: 1,
    rationale: "In a bull call spread, the call you buy (lower strike) is more expensive than the one you sell, resulting in a net cost (debit)."
  },
  {
    id: 40,
    question: "If a client is classified as a \"Commercial Hedger,\" their margin requirements are generally:",
    options: ["Higher than a speculator's.", "Lower than a speculator's.", "The same as a speculator's.", "Determined solely by their age and net worth."],
    correct: 1,
    rationale: "Hedgers are viewed as lower risk because they own the offsetting physical asset, allowing for lower regulatory margin."
  },
  {
    id: 41,
    question: "An investor expects Stock XYZ to remain in a narrow price range over the next two months. Which of the following strategies would be most appropriate for this outlook?",
    options: ["Long Straddle", "Bull Call Spread", "Iron Condor", "Protective Put"],
    correct: 2,
    rationale: "An Iron Condor is a \"market-neutral\" credit strategy that profits if the underlying stock stays between two specific price points."
  },
  {
    id: 42,
    question: "A gold mining company wants to protect itself against a potential drop in gold prices over the next year. The company should:",
    options: ["Buy gold call options.", "Sell gold futures contracts.", "Buy gold futures contracts.", "Sell gold put options."],
    correct: 1,
    rationale: "This is a \"Short Hedge.\" Producers who own the physical asset sell futures to lock in a guaranteed sale price."
  },
  {
    id: 43,
    question: "Which of the following describes a \"Bull Put Spread\"?",
    options: ["Buying a lower-strike put and selling a higher-strike put.", "Selling a lower-strike put and buying a higher-strike put.", "Selling a higher-strike put and buying a lower-strike put.", "Buying a call and a put at the same strike."],
    correct: 2,
    rationale: "A Bull Put Spread is a credit spread where you sell a higher strike (expensive) and buy a lower strike (cheap) to express a bullish view."
  },
  {
    id: 44,
    question: "An investor buys a call with a strike of $50 for $3.00 and buys a put with a strike of $50 for $2.50. What is the \"upper breakeven\" point for this strategy?",
    options: ["$50.00", "$52.50", "$53.00", "$55.50"],
    correct: 3,
    rationale: "This is a Long Straddle. Upper Breakeven = Strike + Total Premium ($50 + $5.50 = $55.50)."
  },
  {
    id: 45,
    question: "\"Cross-hedging\" occurs when a market participant:",
    options: ["Hedges a stock with an option on the same stock.", "Hedges an asset using a derivative on a different but highly correlated asset.", "Trades derivatives in two different countries simultaneously.", "Buys a call and sells a put on the same index."],
    correct: 1,
    rationale: "If a direct future doesn't exist (e.g., jet fuel), a hedger uses a related one (e.g., heating oil)."
  },
  {
    id: 46,
    question: "A \"Collar\" strategy is constructed by:",
    options: ["Buying a straddle and selling a strangle.", "Owning the underlying stock, buying a protective put, and selling a covered call.", "Buying two calls and selling one put.", "Selling two puts and buying one call."],
    correct: 1,
    rationale: "A collar protects a stock position by creating a \"floor\" (put) and a \"ceiling\" (call)."
  },
  {
    id: 47,
    question: "Which of the following practices involves entering a large number of orders and then quickly cancelling them to create a false sense of market depth?",
    options: ["Front-running", "Spoofing", "Wash Trading", "Marking the Close"],
    correct: 1,
    rationale: "Spoofing is the act of placing non-bona fide orders to manipulate price behavior."
  },
  {
    id: 48,
    question: "An advisor is managing a $2,000,000 equity portfolio with a Beta of 0.80. If they want to \"Equitize Cash\" to maintain full market exposure, they should:",
    options: ["Sell index futures.", "Buy index futures.", "Buy protective puts.", "Sell covered calls."],
    correct: 1,
    rationale: "Equitizing cash means using futures to gain market exposure for idle cash balances."
  },
  {
    id: 49,
    question: "Under CIRO's Standards of Conduct, if a Registered Representative (RR) identifies a material conflict of interest that cannot be managed in the client's best interest, the RR MUST:",
    options: ["Disclose the conflict in writing and proceed with the trade.", "Avoid the conflict entirely.", "Charge a lower commission to compensate for the conflict.", "Ask the client to sign a waiver acknowledging the conflict."],
    correct: 1,
    rationale: "Client Focused Reforms mandate that conflicts that cannot be resolved in the client's best interest must be avoided."
  },
  {
    id: 50,
    question: "What is the maximum profit for a \"Bear Call Spread\"?",
    options: ["Unlimited.", "The difference between the strike prices.", "The net credit received.", "The strike price of the long call."],
    correct: 2,
    rationale: "As a credit spread, the maximum gain is limited to the premium collected at the start."
  },
  {
    id: 51,
    question: "An investor owns Stock ABC at $100. They sell a $105 Call for $2.00. What is their \"break-even\" price on this covered call position?",
    options: ["$98.00", "$100.00", "$102.00", "$107.00"],
    correct: 0,
    rationale: "Covered Call Breakeven = Stock Cost − Premium Received ($100 − $2 = $98)."
  },
  {
    id: 52,
    question: "\"Wash Trading\" is strictly prohibited because it:",
    options: ["Causes the advisor to lose money.", "Creates a misleading appearance of active trading without a change in beneficial ownership.", "Allows a client to avoid paying commissions.", "Increases the volatility of the underlying stock."],
    correct: 1,
    rationale: "Wash trades mislead the market by creating \"fake\" volume."
  },
  {
    id: 53,
    question: "A \"Calendar Spread\" (Time Spread) profits primarily from:",
    options: ["A massive move in the stock price.", "The different rates of time decay (Theta) between near-term and long-term options.", "Changes in the risk-free interest rate.", "A stock split or merger."],
    correct: 1,
    rationale: "Calendar spreads bet that the near-term option will decay faster than the further-out option."
  },
  {
    id: 54,
    question: "An RR wants to borrow $5,000 from a long-time client to cover a personal emergency. According to CIRO rules, this is:",
    options: ["Permitted if the client is wealthy.", "Permitted if there is a written loan agreement.", "Prohibited.", "Permitted only if the RR pays a high interest rate."],
    correct: 2,
    rationale: "Personal financial dealings (borrowing/lending) with clients are strictly prohibited."
  },
  {
    id: 55,
    question: "Which of the following is a \"Synthetic Long Call\"?",
    options: ["Long Stock + Short Put", "Long Stock + Long Put", "Short Stock + Long Put", "Long Call + Short Put"],
    correct: 1,
    rationale: "Owning the stock and a put creates a payoff profile identical to a long call."
  },
  {
    id: 56,
    question: "\"Duration\" in the context of fixed-income derivatives is a measure of:",
    options: ["How long until a bond matures.", "The sensitivity of a bond's price to changes in interest rates.", "The credit rating of the bond issuer.", "The annual dividend yield of the bond."],
    correct: 1,
    rationale: "Duration measures price sensitivity; managers use bond futures to hedge this risk."
  },
  {
    id: 57,
    question: "A \"Butterfly Spread\" is constructed using:",
    options: ["Two different underlying assets.", "Three different strike prices of the same type (Calls or Puts).", "Four different expiration months.", "Only out-of-the-money options."],
    correct: 1,
    rationale: "A butterfly uses a low, middle, and high strike (e.g., Buy 1, Sell 2, Buy 1)."
  },
  {
    id: 58,
    question: "If an investor is \"Short\" a put option and is \"Assigned,\" they are obligated to:",
    options: ["Sell the underlying stock at the strike price.", "Buy the underlying stock at the strike price.", "Pay the difference between the strike and market price in cash.", "Buy another put option to close the position."],
    correct: 1,
    rationale: "Put writers have the obligation to buy the stock if the holder exercises."
  },
  {
    id: 59,
    question: "\"Marking the Close\" is a manipulative practice aimed at:",
    options: ["Trading ahead of a large client order.", "Influencing the final settlement price of a contract at the end of the day.", "Hiding the identity of a beneficial owner.", "Executing trades during the pre-market session."],
    correct: 1,
    rationale: "This manipulates the daily settlement price, which affects margin and valuation."
  },
  {
    id: 60,
    question: "Which strategy has \"Unlimited\" risk?",
    options: ["Bull Call Spread", "Long Straddle", "Short Strangle", "Protective Put"],
    correct: 2,
    rationale: "A Short Strangle involves selling uncovered calls and puts; if the stock rockets up, the risk is unlimited."
  },
  {
    id: 61,
    question: "An advisor receives a referral from an accountant and wants to pay them a fee. This is only allowed if:",
    options: ["The fee is less than $100.", "The arrangement is in writing and disclosed to the client.", "The accountant is also a Registered Representative.", "The client makes a profit on the trade."],
    correct: 1,
    rationale: "Referral fees are legal only with a formal agreement and full client disclosure."
  },
  {
    id: 62,
    question: "A \"Ratio Spread\" involves:",
    options: ["Trading options on two different stocks.", "Buying and selling an unequal number of option contracts.", "Combining stocks and bonds in one account.", "Trading only during the last hour of the day."],
    correct: 1,
    rationale: "A ratio spread (e.g., Buy 1, Sell 2) creates a unique, non-linear risk profile."
  },
  {
    id: 63,
    question: "\"Basis Risk\" in a hedge increases when:",
    options: ["The futures price and spot price move perfectly together.", "The derivative and the underlying asset do not move in exact correlation.", "The investor uses a market order instead of a limit order.", "The clearinghouse increases margin requirements."],
    correct: 1,
    rationale: "If the correlation breaks, the hedge becomes less effective."
  },
  {
    id: 64,
    question: "A client wants to trade derivatives but refuses to provide their full financial information for the KYC. The RR should:",
    options: ["Open the account anyway but limit the trade size.", "Refuse to open the account.", "Use the information from a similar client's profile.", "Ask the client's spouse for the information instead."],
    correct: 1,
    rationale: "An RR cannot fulfill their suitability duty without complete KYC information."
  },
  {
    id: 65,
    question: "\"Gamma Scalping\" is a technique used to:",
    options: ["Avoid paying commissions.", "Maintain a Delta-neutral position while profiting from stock price swings.", "Manipulate the opening price of a stock.", "Hedge a currency swap."],
    correct: 1,
    rationale: "Professional traders use gamma to \"buy low/sell high\" while staying directionally neutral."
  },
  {
    id: 66,
    question: "Which of the following is considered a \"Non-Bona Fide\" order?",
    options: ["A limit order placed far away from the current market price.", "An order entered with the intent to cancel it before execution (Spoofing).", "A market order placed by a retail investor.", "A stop-loss order placed by a hedge fund."],
    correct: 1,
    rationale: "Orders not intended to be filled are deceptive and illegal."
  },
  {
    id: 67,
    question: "A \"Synthetic Short Stock\" position is created by:",
    options: ["Selling a Call and Buying a Put at the same strike.", "Buying a Call and Selling a Put at the same strike.", "Selling a Call and Selling a Put at the same strike.", "Shorting a future and buying a call."],
    correct: 0,
    rationale: "Short Call + Long Put mimics the P&L of being short the stock."
  },
  {
    id: 68,
    question: "The \"Crack Spread\" in futures refers to the price difference between:",
    options: ["Wheat and Corn.", "Crude Oil and its refined products (Gasoline/Heating Oil).", "Gold and Silver.", "Two different months of the same commodity."],
    correct: 1,
    rationale: "The crack spread represents the refining margin for oil companies."
  },
  {
    id: 69,
    question: "An RR notices a client is making trades that are clearly unsuitable for their risk profile. The RR's first duty is to:",
    options: ["Inform the client's employer.", "Warn the client and document the conversation.", "Execute the trades anyway as the client is always right.", "Transfer the client's money to a savings account."],
    correct: 1,
    rationale: "RRs have a \"Duty to Warn\" when they observe unsuitable trading activity."
  },
  {
    id: 70,
    question: "A \"Long Strangle\" is cheaper than a \"Long Straddle\" because:",
    options: ["It uses fewer contracts.", "Both options are out-of-the-money (OTM).", "It has no time decay.", "It is only traded in the OTC market."],
    correct: 1,
    rationale: "Buying OTM options is cheaper than buying ATM options, though it requires a larger price move to profit."
  },
  {
    id: 71,
    question: "\"Self-Dealing\" occurs when a firm:",
    options: ["Trades with its own clients at fair market prices.", "Uses a client's confidential information to trade for the firm's own advantage.", "Allows employees to have their own brokerage accounts.", "Offers a discount to frequent traders."],
    correct: 1,
    rationale: "This is an unethical abuse of client trust and confidential order flow."
  },
  {
    id: 72,
    question: "An investor buys a call for $4.00 and sells a call at a higher strike for $1.00. The distance between the strikes is $10.00. What is the \"Max Profit\" for this spread?",
    options: ["$300", "$600", "$700", "$900"],
    correct: 2,
    rationale: "Max Profit = (Strike Diff − Net Debit) × 100. ($10 − $3) × 100 = $700."
  },
  {
    id: 73,
    question: "Which \"Greek\" measures the sensitivity of an option to the passage of time?",
    options: ["Delta", "Gamma", "Theta", "Vega"],
    correct: 2,
    rationale: "Theta is the \"Silent Killer\" that erodes option value daily."
  },
  {
    id: 74,
    question: "A \"Delta-Neutral\" portfolio has a total Delta of:",
    options: ["1.00", "0.50", "0.00", "-1.00"],
    correct: 2,
    rationale: "A neutral portfolio is balanced so it doesn't gain or lose value from small directional moves."
  },
  {
    id: 75,
    question: "\"Outside Business Activities\" (OBAs) must be:",
    options: ["Kept secret from the firm.", "Disclosed to and approved by the firm's compliance department.", "Limited to volunteer work only.", "Reported only if they earn more than $50,000."],
    correct: 1,
    rationale: "Firms must monitor OBAs to ensure they don't create conflicts of interest."
  },
  {
    id: 76,
    question: "An investor who buys a \"Deep In-the-Money\" option will find that its Delta is closest to:",
    options: ["0.00", "0.50", "1.00", "-1.00"],
    correct: 2,
    rationale: "Deep ITM options move almost dollar-for-dollar with the underlying stock."
  },
  {
    id: 77,
    question: "The \"Ted Spread\" is a measure of credit risk between:",
    options: ["Stocks and Bonds.", "US Treasuries and Eurodollar (or LIBOR/CORRA) rates.", "The US Dollar and the Canadian Dollar.", "Gold and Crude Oil."],
    correct: 1,
    rationale: "It reflects the \"perceived risk\" in the interbank lending market."
  },
  {
    id: 78,
    question: "When an advisor provides a \"Risk Disclosure Statement,\" they are fulfilling a:",
    options: ["Marketing requirement.", "Fiduciary-like regulatory obligation.", "Tax obligation.", "Personal preference."],
    correct: 1,
    rationale: "Ensuring a client understands risk is a core regulatory pillar."
  },
  {
    id: 79,
    question: "A \"Synthetic Long Put\" is constructed by:",
    options: ["Short Stock + Long Call", "Long Stock + Short Call", "Long Stock + Long Put", "Short Stock + Short Call"],
    correct: 0,
    rationale: "Being short the stock and owning a call creates a \"downside profit\" profile identical to a put."
  },
  {
    id: 80,
    question: "In a \"Backwardation\" market, the spot price is:",
    options: ["Higher than the futures price.", "Lower than the futures price.", "Moving slower than the futures price.", "Unaffected by supply and demand."],
    correct: 0,
    rationale: "Backwardation occurs during immediate shortages, where the \"price today\" is higher than the \"price tomorrow.\""
  },
  {
    id: 81,
    question: "A portfolio manager wants to reduce a portfolio's Beta from 1.40 to 1.00. The portfolio value is $20,000,000 and the S&P/TSX 60 Index Future is at 1,000 with a $200 multiplier. How many contracts must be traded?",
    options: ["40", "60", "80", "100"],
    correct: 0,
    rationale: "Contracts = (Portfolio Value / [Futures Price × Multiplier]) × (Target β − Current β). ($20,000,000 / $200,000) × (1.00 − 1.40) = 100 × (−0.40) = −40. The negative sign indicates selling 40 contracts."
  },
  {
    id: 82,
    question: "Which of the following is an \"Agency\" trade?",
    options: ["The firm buys an option from its own inventory to sell to a client.", "The firm executes a trade for a client on the exchange and charges a commission.", "The firm trades against a client to facilitate a large block trade.", "The firm uses its own capital to arbitrage a price difference."],
    correct: 1,
    rationale: "In an Agency trade, the firm acts only as a middleman between the client and the market."
  },
  {
    id: 83,
    question: "An investor is \"Short\" a call and \"Short\" a put at the same strike. This position is called a:",
    options: ["Long Straddle.", "Short Straddle.", "Bull Spread.", "Ratio Spread."],
    correct: 1,
    rationale: "Selling both options at the same strike is a Short Straddle, used when expecting low volatility."
  },
  {
    id: 84,
    question: "If the \"Risk-Free Interest Rate\" rises, what is the theoretical impact on call and put premiums?",
    options: ["Calls rise; Puts fall.", "Calls fall; Puts rise.", "Both rise.", "Both fall."],
    correct: 0,
    rationale: "Higher rates increase the \"Cost of Carry,\" making calls (a stock alternative) more expensive and puts cheaper."
  },
  {
    id: 85,
    question: "\"Position Limits\" are enforced by the exchange to primarily prevent:",
    options: ["High-frequency trading.", "Market cornering or manipulation by a single entity.", "Small retail investors from losing money.", "The clearinghouse from having to pay out too much cash."],
    correct: 1,
    rationale: "Limits ensure no one participant can control enough of the market to artificially dictate prices."
  },
  {
    id: 86,
    question: "A \"Married Put\" strategy is identical in risk profile to a:",
    options: ["Covered Call.", "Long Call.", "Bear Put Spread.", "Naked Put."],
    correct: 1,
    rationale: "Buying a stock and a put (Married Put) has the same payoff graph as a Long Call."
  },
  {
    id: 87,
    question: "An option has a Delta of -0.35. This option is most likely a:",
    options: ["Call that is Out-of-the-Money.", "Put that is Out-of-the-Money.", "Call that is In-the-Money.", "Put that is In-the-Money."],
    correct: 1,
    rationale: "Puts have negative deltas. A delta of −0.35 (less than −0.50) indicates it is OTM."
  },
  {
    id: 88,
    question: "\"Quote Stuffing\" is a prohibited practice that involves:",
    options: ["Providing false information on a KYC form.", "Flooding the market with orders to create latency for other traders.", "Trading on behalf of a deceased client.", "Combining two different clients' orders into one."],
    correct: 1,
    rationale: "This is a manipulative technique used to slow down exchange systems to gain a speed advantage."
  },
  {
    id: 89,
    question: "An investor buys a Call for $5.00. The Delta is 0.50 and Gamma is 0.10. If the stock price rises by $2.00, what is the new Delta?",
    options: ["0.50", "0.60", "0.70", "0.80"],
    correct: 2,
    rationale: "New Delta = Old Delta + (Gamma × ΔStock). 0.50 + (0.10 × 2) = 0.70."
  },
  {
    id: 90,
    question: "Which document is required for a corporation to name the specific individuals authorized to trade derivatives for the company?",
    options: ["Managed Account Agreement.", "Hedge Agreement.", "Corporate Resolution.", "Relationship Disclosure Information."],
    correct: 2,
    rationale: "The Corporate Resolution is a legal document from the board of directors authorizing specific traders."
  },
  {
    id: 91,
    question: "A \"Long Strangle\" consists of:",
    options: ["Selling an ITM Call and ITM Put.", "Buying an OTM Call and OTM Put.", "Buying a Call and Future simultaneously.", "Selling a Put and buying the underlying stock."],
    correct: 1,
    rationale: "Strangles use OTM options to reduce the cost of betting on a large volatility move."
  },
  {
    id: 92,
    question: "In a \"Cash-and-Carry\" arbitrage, a trader would:",
    options: ["Buy the future and sell the underlying stock.", "Buy the underlying stock and sell the future.", "Sell both the stock and the future.", "Buy both the stock and the future."],
    correct: 1,
    rationale: "This is done when the future is \"too expensive\" relative to the spot price plus the cost of carry."
  },
  {
    id: 93,
    question: "\"Assignment\" of an exercise notice by the CDCC to a clearing member is done on:",
    options: ["A first-in, first-out (FIFO) basis.", "A random basis.", "The basis of the largest position size.", "The basis of the longest-held position."],
    correct: 1,
    rationale: "Random assignment ensures fairness and prevents participants from predicting when they will be assigned."
  },
  {
    id: 94,
    question: "What is the maximum loss for a \"Bull Put Spread\"?",
    options: ["The net credit received.", "The difference in strikes minus the net credit.", "Unlimited.", "The strike price of the short put."],
    correct: 1,
    rationale: "Because it's a credit spread, the max loss is the \"gap\" you didn't cover with the credit."
  },
  {
    id: 95,
    question: "A \"Jitney\" trade must be identified as such to:",
    options: ["Allow the firm to charge double commissions.", "Ensure regulators can trace the trade back to the originating firm and client.", "Bypass the CDCC clearing process.", "Avoid the mark-to-market settlement."],
    correct: 1,
    rationale: "Proper labeling is essential for maintaining the audit trail across multiple firms."
  },
  {
    id: 96,
    question: "\"Implied Volatility\" is derived from:",
    options: ["Historical price charts of the last 10 years.", "The current market price of the option itself.", "The annual dividend yield of the company.", "The bank's prime lending rate."],
    correct: 1,
    rationale: "IV is the volatility level that, when plugged into a pricing model, produces the current market price."
  },
  {
    id: 97,
    question: "An investor buys a \"Protective Put\" on a stock they bought at $80. The put strike is $75 and cost $2.00. What is their maximum possible loss?",
    options: ["$2.00", "$5.00", "$7.00", "$75.00"],
    correct: 2,
    rationale: "Max Loss = (Purchase Price − Strike) + Put Premium. ($80 − $75) + $2 = $7."
  },
  {
    id: 98,
    question: "The \"Maintenance Margin\" for a future is typically what percentage of the Initial Margin?",
    options: ["50%", "75%–80%", "100%", "120%"],
    correct: 1,
    rationale: "Maintenance levels are lower than initial levels to allow for minor market fluctuations without constant margin calls."
  },
  {
    id: 99,
    question: "Which of the following is a \"Market-Neutral\" strategy?",
    options: ["Bull Call Spread.", "Long Straddle.", "Covered Call.", "Protective Put."],
    correct: 1,
    rationale: "A straddle doesn't care about direction; it only cares about the magnitude of the move."
  },
  {
    id: 100,
    question: "A \"Suitability\" assessment must be conducted for a client:",
    options: ["Only when the account is first opened.", "Every time a recommendation is made or a material change occurs.", "Only if the client requests it.", "Once every five years."],
    correct: 1,
    rationale: "Suitability is an ongoing obligation triggered by trades, recommendations, or life events."
  },
  {
    id: 101,
    question: "\"Time Value\" decay (Theta) accelerates most rapidly:",
    options: ["One year before expiration.", "Six months before expiration.", "In the final 30 days before expiration.", "Immediately after a stock split."],
    correct: 2,
    rationale: "The \"Theta curve\" steepens significantly as expiration approaches, particularly for ATM options."
  },
  {
    id: 102,
    question: "An investor sells 5 gold futures at $2,000. Each contract is 100 oz. The price falls to $1,980. What is the profit?",
    options: ["$2,000", "$5,000", "$10,000", "$20,000"],
    correct: 2,
    rationale: "(Entry − Exit) × 100 × 5 = ($2,000 − $1,980) × 500 = $20 × 500 = $10,000."
  },
  {
    id: 103,
    question: "If an RR suspects a client is being financially exploited by a family member, they should first contact:",
    options: ["The police.", "The firm's Compliance Department or the Trusted Contact Person (TCP).", "CIRO's anonymous tip line.", "The family member to ask for an explanation."],
    correct: 1,
    rationale: "Internal compliance and the TCP are the first line of defense for vulnerable clients."
  },
  {
    id: 104,
    question: "Which Greek is zero for the underlying stock itself?",
    options: ["Delta.", "Gamma.", "Rho.", "All Greeks except Delta."],
    correct: 1,
    rationale: "Stock has a constant Delta of 1.0; therefore, its Gamma (change in Delta) is zero."
  },
  {
    id: 105,
    question: "A \"Wash Trade\" involves buy and sell orders that result in:",
    options: ["A large commission for the advisor.", "No change in beneficial ownership.", "A significant price increase in the stock.", "Automatic exercise of all options."],
    correct: 1,
    rationale: "This creates a deceptive illusion of liquidity and is strictly prohibited."
  },
  {
    id: 106,
    question: "In an \"Equity Swap,\" one party typically receives the returns of an index and pays:",
    options: ["A fixed or floating interest rate.", "The returns of a different index.", "A physical commodity like gold.", "Nothing; swaps are free."],
    correct: 0,
    rationale: "Most equity swaps involve exchanging stock returns for a \"funding leg\" (interest)."
  },
  {
    id: 107,
    question: "An investor wants to buy a stock at $90, which is currently at $95. They sell a $90 put for $2.00. If the stock is at $85 at expiry, what is their \"Effective Cost\" per share?",
    options: ["$83", "$85", "$88", "$90"],
    correct: 2,
    rationale: "They are assigned at $90 but kept the $2 premium. Cost = $90 − $2 = $88."
  },
  {
    id: 108,
    question: "Which organization establishes the \"UMIR\" rules?",
    options: ["The Montreal Exchange.", "CIRO.", "The CDCC.", "The Canadian Securities Administrators (CSA)."],
    correct: 1,
    rationale: "CIRO (formerly IIROC) oversees the Universal Market Integrity Rules for all Canadian marketplaces."
  },
  {
    id: 109,
    question: "An option is \"In-the-Money\" if:",
    options: ["Its intrinsic value is positive.", "Its time value is greater than its intrinsic value.", "The volatility of the stock is rising.", "The expiration date is at least six months away."],
    correct: 0,
    rationale: "ITM simply means the option currently has real, exercisable value."
  },
  {
    id: 110,
    question: "A \"Calendar Spread\" is also known as a:",
    options: ["Vertical Spread.", "Horizontal Spread.", "Diagonal Spread.", "Butterfly Spread."],
    correct: 1,
    rationale: "Because it uses different expirations (horizontal on the table) but the same strike."
  },
  {
    id: 111,
    question: "\"Front-running\" is a violation of which ethical pillar?",
    options: ["Client Priority.", "Best Execution.", "KYC.", "Suitability."],
    correct: 0,
    rationale: "Advisors must always place client orders before their own."
  },
  {
    id: 112,
    question: "An investor buys a \"Butterfly Spread\" because they expect the stock to:",
    options: ["Skyrocket.", "Crash.", "Stay exactly at the middle strike price.", "Become extremely volatile."],
    correct: 2,
    rationale: "A butterfly is a neutral strategy that reaches maximum profit if the stock \"pins\" the middle strike."
  },
  {
    id: 113,
    question: "\"Position Reporting\" to the exchange is required for accounts that:",
    options: ["Trade more than 10 times a day.", "Hold positions exceeding a specified \"Reporting Threshold.\"", "Are owned by institutional clients only.", "Only trade index futures."],
    correct: 1,
    rationale: "Regulators monitor large positions (LPR) to ensure market stability."
  },
  {
    id: 114,
    question: "If a client is \"Assigned\" on a Short Call, they must:",
    options: ["Pay the premium back.", "Deliver the underlying shares at the strike price.", "Buy a put to offset the risk.", "Receive cash from the clearinghouse."],
    correct: 1,
    rationale: "Call writers have an obligation to sell/deliver the shares."
  },
  {
    id: 115,
    question: "\"Theta\" is always a negative number for:",
    options: ["Option Writers.", "Option Buyers.", "Futures Traders.", "Short Stock positions."],
    correct: 1,
    rationale: "Buyers lose value as time passes (decay). Writers actually have \"Positive Theta\" as decay benefits them."
  },
  {
    id: 116,
    question: "A \"Stop-Limit\" order differs from a \"Stop\" order because it:",
    options: ["Guarantees execution.", "Does not become a market order; it becomes a limit order at the trigger price.", "Can only be used for selling, not buying.", "Is only valid for 15 minutes."],
    correct: 1,
    rationale: "This protects the trader from being filled at an extreme price in a \"gapping\" market."
  },
  {
    id: 117,
    question: "An investor is \"Long\" a call with a Delta of 0.80. To make this position \"Delta-Neutral,\" they must:",
    options: ["Sell 80 shares of the underlying stock.", "Buy 80 shares of the underlying stock.", "Sell a put with a Delta of -0.20.", "Buy 20 more calls."],
    correct: 0,
    rationale: "+0.80 (Option Delta) and −0.80 (Short 80 shares) equals zero."
  },
  {
    id: 118,
    question: "Which of the following is an \"Outside Business Activity\" that MUST be disclosed?",
    options: ["Volunteering at a local animal shelter once a year.", "Being a paid director for a private software company.", "Having a personal savings account at a rival bank.", "Buying a 1% stake in a publicly traded utility company."],
    correct: 1,
    rationale: "Paid positions or directorships are major sources of potential conflict."
  },
  {
    id: 119,
    question: "A \"Synthetic Short Put\" is created by:",
    options: ["Short Stock + Short Call.", "Long Stock + Short Call.", "Short Stock + Long Call.", "Long Stock + Long Call."],
    correct: 1,
    rationale: "Long Stock + Short Call (Covered Call) has the same risk/reward profile as selling a put."
  },
  {
    id: 120,
    question: "The primary goal of \"Regulatory Capital\" is to ensure:",
    options: ["The firm makes a profit every year.", "The firm has enough liquid assets to meet its obligations to clients and the clearinghouse.", "Advisors are paid their commissions on time.", "The exchange can pay for its electronic trading system."],
    correct: 1,
    rationale: "Capital requirements protect the integrity of the entire financial system from firm failures."
  }
];

const LABELS = ["A", "B", "C", "D"];

const styles = {
  root: {
    minHeight: "100vh",
    background: "#f8fafc",
    fontFamily: "'system-ui', 'Segoe UI', sans-serif",
    color: "#0f172a",
  },
  header: {
    position: "sticky",
    top: 0,
    zIndex: 100,
    background: "#fff",
    borderBottom: "1px solid #e2e8f0",
    boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
  },
  headerInner: {
    maxWidth: 900,
    margin: "0 auto",
    padding: "12px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  logo: {
    fontFamily: "Georgia, serif",
    fontWeight: 700,
    fontSize: 15,
    color: "#0e7490",
    letterSpacing: "-0.3px",
    whiteSpace: "nowrap",
  },
  examTitle: {
    fontFamily: "Georgia, serif",
    fontWeight: 700,
    fontSize: 13,
    color: "#0f172a",
    opacity: 0.7,
  },
  progressWrap: {
    flex: 1,
    maxWidth: 260,
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  progressBar: {
    height: 6,
    background: "#e2e8f0",
    borderRadius: 99,
    overflow: "hidden",
  },
  progressFill: (pct) => ({
    height: "100%",
    width: `${pct}%`,
    background: "linear-gradient(90deg, #0e7490, #0891b2)",
    borderRadius: 99,
    transition: "width 0.4s ease",
  }),
  progressText: {
    fontSize: 11,
    color: "#64748b",
    textAlign: "right",
  },
  main: {
    maxWidth: 900,
    margin: "0 auto",
    padding: "28px 20px 60px",
  },
  // Mode select screen
  modeCard: {
    background: "#fff",
    borderRadius: 16,
    border: "1px solid #e2e8f0",
    padding: "40px 36px",
    textAlign: "center",
    maxWidth: 560,
    margin: "40px auto",
    boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
  },
  modeHeading: {
    fontFamily: "Georgia, serif",
    fontSize: 26,
    fontWeight: 700,
    color: "#0f172a",
    marginBottom: 8,
  },
  modeSub: {
    fontSize: 14,
    color: "#64748b",
    marginBottom: 32,
    lineHeight: 1.6,
  },
  modeBtns: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  modeBtn: (primary) => ({
    padding: "16px 28px",
    borderRadius: 12,
    border: primary ? "none" : "1.5px solid #0e7490",
    background: primary ? "linear-gradient(135deg, #0e7490, #0891b2)" : "#fff",
    color: primary ? "#fff" : "#0e7490",
    fontFamily: "Georgia, serif",
    fontSize: 16,
    fontWeight: 700,
    cursor: "pointer",
    transition: "all 0.2s",
    boxShadow: primary ? "0 4px 14px rgba(14,116,144,0.28)" : "none",
  }),
  modeBtnLabel: {
    fontSize: 12,
    fontWeight: 400,
    opacity: 0.8,
    display: "block",
    marginTop: 2,
  },
  // Navigator
  navWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginBottom: 24,
    padding: "14px 16px",
    background: "#fff",
    borderRadius: 12,
    border: "1px solid #e2e8f0",
  },
  navDot: (state) => {
    const base = {
      width: 28,
      height: 28,
      borderRadius: 6,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 10,
      fontWeight: 700,
      cursor: "pointer",
      border: "none",
      transition: "all 0.15s",
    };
    if (state === "current") return { ...base, background: "#0e7490", color: "#fff" };
    if (state === "correct") return { ...base, background: "#dcfce7", color: "#166534" };
    if (state === "wrong") return { ...base, background: "#fee2e2", color: "#991b1b" };
    if (state === "answered") return { ...base, background: "#dbeafe", color: "#1e40af" };
    return { ...base, background: "#f1f5f9", color: "#64748b" };
  },
  // Question card
  qCard: {
    background: "#fff",
    borderRadius: 16,
    border: "1px solid #e2e8f0",
    padding: "28px 30px",
    marginBottom: 18,
    boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
  },
  qNum: {
    fontSize: 11,
    fontWeight: 700,
    color: "#0e7490",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    marginBottom: 10,
  },
  qText: {
    fontFamily: "Georgia, serif",
    fontSize: 17,
    fontWeight: 700,
    color: "#0f172a",
    lineHeight: 1.55,
    marginBottom: 22,
  },
  optionBtn: (state) => {
    const base = {
      width: "100%",
      textAlign: "left",
      padding: "13px 16px",
      borderRadius: 10,
      marginBottom: 10,
      border: "1.5px solid",
      cursor: "pointer",
      fontSize: 14,
      lineHeight: 1.45,
      transition: "all 0.18s",
      display: "flex",
      alignItems: "flex-start",
      gap: 10,
    };
    if (state === "correct") return { ...base, background: "#dcfce7", borderColor: "#166534", color: "#166534" };
    if (state === "wrong") return { ...base, background: "#fee2e2", borderColor: "#991b1b", color: "#991b1b" };
    if (state === "selected") return { ...base, background: "#dbeafe", borderColor: "#1e40af", color: "#1e40af" };
    if (state === "dimmed") return { ...base, background: "#f8fafc", borderColor: "#e2e8f0", color: "#94a3b8", cursor: "default" };
    return { ...base, background: "#f8fafc", borderColor: "#e2e8f0", color: "#374151" };
  },
  label: (state) => {
    const base = {
      minWidth: 22,
      height: 22,
      borderRadius: 5,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 11,
      fontWeight: 800,
      flexShrink: 0,
      marginTop: 1,
    };
    if (state === "correct") return { ...base, background: "#166534", color: "#fff" };
    if (state === "wrong") return { ...base, background: "#991b1b", color: "#fff" };
    if (state === "selected") return { ...base, background: "#1e40af", color: "#fff" };
    if (state === "dimmed") return { ...base, background: "#e2e8f0", color: "#94a3b8" };
    return { ...base, background: "#e2e8f0", color: "#64748b" };
  },
  rationale: {
    marginTop: 18,
    padding: "14px 16px",
    background: "#f0f9ff",
    borderRadius: 10,
    borderLeft: "4px solid #0e7490",
    fontSize: 13.5,
    color: "#0c4a6e",
    lineHeight: 1.6,
  },
  rationaleTitle: {
    fontWeight: 700,
    marginBottom: 4,
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: "#0e7490",
  },
  navBtns: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    marginTop: 6,
  },
  btn: (variant) => {
    if (variant === "primary") return {
      padding: "11px 26px", borderRadius: 9, border: "none",
      background: "linear-gradient(135deg, #0e7490, #0891b2)",
      color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer",
      boxShadow: "0 3px 10px rgba(14,116,144,0.25)",
    };
    if (variant === "ghost") return {
      padding: "11px 26px", borderRadius: 9,
      border: "1.5px solid #cbd5e1", background: "#fff",
      color: "#64748b", fontWeight: 600, fontSize: 14, cursor: "pointer",
    };
    if (variant === "danger") return {
      padding: "11px 26px", borderRadius: 9, border: "none",
      background: "#0f172a", color: "#fff", fontWeight: 700,
      fontSize: 14, cursor: "pointer",
    };
    return {};
  },
  // Results
  resultsCard: {
    background: "#fff",
    borderRadius: 16,
    border: "1px solid #e2e8f0",
    padding: "36px 32px",
    marginBottom: 24,
    boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
  },
  scoreRing: (pct) => ({
    width: 120,
    height: 120,
    borderRadius: "50%",
    background: `conic-gradient(${pct >= 70 ? "#0e7490" : "#991b1b"} ${pct * 3.6}deg, #e2e8f0 0deg)`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 20px",
    position: "relative",
  }),
  scoreInner: {
    width: 86,
    height: 86,
    borderRadius: "50%",
    background: "#fff",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  scorePct: (pct) => ({
    fontFamily: "Georgia, serif",
    fontSize: 26,
    fontWeight: 700,
    color: pct >= 70 ? "#0e7490" : "#991b1b",
    lineHeight: 1,
  }),
  scoreLabel: { fontSize: 10, color: "#64748b", fontWeight: 600 },
  statGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: 14,
    marginTop: 22,
  },
  statBox: (color) => ({
    padding: "14px 12px",
    borderRadius: 10,
    background: color === "green" ? "#dcfce7" : color === "red" ? "#fee2e2" : "#f1f5f9",
    textAlign: "center",
  }),
  statNum: (color) => ({
    fontFamily: "Georgia, serif",
    fontSize: 28,
    fontWeight: 700,
    color: color === "green" ? "#166534" : color === "red" ? "#991b1b" : "#374151",
  }),
  statLbl: { fontSize: 11, color: "#64748b", fontWeight: 600 },
  reviewItem: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    padding: "18px 20px",
    marginBottom: 14,
  },
  reviewItemCorrect: {
    background: "#fff",
    border: "1px solid #bbf7d0",
    borderLeft: "4px solid #166534",
    borderRadius: 12,
    padding: "18px 20px",
    marginBottom: 14,
  },
  reviewItemWrong: {
    background: "#fff",
    border: "1px solid #fecaca",
    borderLeft: "4px solid #991b1b",
    borderRadius: 12,
    padding: "18px 20px",
    marginBottom: 14,
  },
  badge: (type) => ({
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: 99,
    fontSize: 11,
    fontWeight: 700,
    background: type === "correct" ? "#dcfce7" : "#fee2e2",
    color: type === "correct" ? "#166534" : "#991b1b",
    marginBottom: 8,
  }),
};

export default function App() {
  const [screen, setScreen] = useState("select"); // select | practice | exam | results
  const [mode, setMode] = useState(null); // 'practice' | 'exam'
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showRationale, setShowRationale] = useState(false);
  const [examSubmitted, setExamSubmitted] = useState(false);
  const [reviewFilter, setReviewFilter] = useState("wrong"); // 'all' | 'wrong'
  const topRef = useRef(null);

  const scrollTop = () => topRef.current?.scrollIntoView({ behavior: "smooth" });

  const answered = Object.keys(answers).length;
  const pct = Math.round((answered / questions.length) * 100);

  const score = Object.entries(answers).filter(([i, a]) => questions[i].correct === a).length;
  const scorePct = Math.round((score / questions.length) * 100);

  function startMode(m) {
    setMode(m);
    setScreen(m);
    setCurrent(0);
    setAnswers({});
    setShowRationale(false);
    setExamSubmitted(false);
  }

  function selectAnswer(optIdx) {
    if (mode === "practice") {
      if (answers[current] !== undefined) return;
      setAnswers(prev => ({ ...prev, [current]: optIdx }));
      setShowRationale(true);
      scrollTop();
    } else {
      setAnswers(prev => ({ ...prev, [current]: optIdx }));
    }
  }

  function goTo(i) {
    setCurrent(i);
    setShowRationale(mode === "practice" && answers[i] !== undefined);
    scrollTop();
  }

  function next() {
    if (current < questions.length - 1) {
      goTo(current + 1);
    }
  }

  function prev() {
    if (current > 0) goTo(current - 1);
  }

  function submitExam() {
    setExamSubmitted(true);
    setScreen("results");
    scrollTop();
  }

  function getNavState(i) {
    if (i === current && mode === "practice") return "current";
    if (answers[i] === undefined) return i === current ? "current" : "unanswered";
    if (mode === "exam" && !examSubmitted) return "answered";
    if (questions[i].correct === answers[i]) return "correct";
    return "wrong";
  }

  const q = questions[current];

  function getOptionState(idx) {
    if (mode === "practice") {
      if (answers[current] === undefined) return "default";
      if (idx === q.correct) return "correct";
      if (idx === answers[current]) return "wrong";
      return "dimmed";
    } else {
      if (!examSubmitted) return answers[current] === idx ? "selected" : "default";
      if (idx === q.correct) return "correct";
      if (idx === answers[current] && answers[current] !== q.correct) return "wrong";
      return "dimmed";
    }
  }

  // ── SELECT SCREEN ──────────────────────────────────────────────────
  if (screen === "select") {
    return (
      <div style={styles.root}>
        <div style={styles.header}>
          <div style={styles.headerInner}>
            <span style={styles.logo}>Vitta Finance Academy</span>
            <span style={styles.examTitle}>CIRO Derivatives · Mock Exam 1</span>
          </div>
        </div>
        <div style={styles.main}>
          <div style={styles.modeCard}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>📊</div>
            <h1 style={styles.modeHeading}>CIRO Derivatives<br />Mock Exam 1</h1>
            <p style={styles.modeSub}>
              120 questions covering all exam elements — client relationships, pricing theory, trading & clearing, strategy, and market integrity.
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap", marginBottom: 28 }}>
              {["120 Questions", "Elements 1–8", "~3 Hours"].map(tag => (
                <span key={tag} style={{ padding: "4px 12px", background: "#f0f9ff", color: "#0e7490", borderRadius: 99, fontSize: 12, fontWeight: 600, border: "1px solid #bae6fd" }}>{tag}</span>
              ))}
            </div>
            <div style={styles.modeBtns}>
              <button style={styles.modeBtn(true)} onClick={() => startMode("practice")}>
                Practice Mode
                <span style={styles.modeBtnLabel}>Instant feedback & rationale after each question</span>
              </button>
              <button style={styles.modeBtn(false)} onClick={() => startMode("exam")}>
                Exam Mode
                <span style={styles.modeBtnLabel}>Simulate the real exam — see results at the end</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── RESULTS SCREEN ─────────────────────────────────────────────────
  if (screen === "results") {
    const wrong = questions.filter((q, i) => answers[i] !== undefined && answers[i] !== q.correct);
    const correct = questions.filter((q, i) => answers[i] === q.correct);
    const unanswered = questions.length - Object.keys(answers).length;
    const showList = reviewFilter === "wrong"
      ? questions.map((q, i) => ({ q, i })).filter(({ i }) => answers[i] !== undefined && answers[i] !== q.correct)
      : questions.map((q, i) => ({ q, i }));

    return (
      <div style={styles.root} ref={topRef}>
        <div style={styles.header}>
          <div style={styles.headerInner}>
            <span style={styles.logo}>Vitta Finance Academy</span>
            <span style={styles.examTitle}>CIRO Derivatives · Mock Exam 1 · Results</span>
            <button style={styles.btn("ghost")} onClick={() => setScreen("select")}>← New Exam</button>
          </div>
        </div>
        <div style={styles.main}>
          <div style={styles.resultsCard}>
            <h2 style={{ fontFamily: "Georgia, serif", fontSize: 22, fontWeight: 700, marginBottom: 6 }}>Exam Complete</h2>
            <p style={{ color: "#64748b", fontSize: 14, marginBottom: 24 }}>
              {scorePct >= 70 ? "🎉 Great work — you passed!" : "Keep studying — review the rationales below."}
            </p>
            <div style={styles.scoreRing(scorePct)}>
              <div style={styles.scoreInner}>
                <span style={styles.scorePct(scorePct)}>{scorePct}%</span>
                <span style={styles.scoreLabel}>SCORE</span>
              </div>
            </div>
            <div style={styles.statGrid}>
              <div style={styles.statBox("green")}>
                <div style={styles.statNum("green")}>{correct.length}</div>
                <div style={styles.statLbl}>Correct</div>
              </div>
              <div style={styles.statBox("red")}>
                <div style={styles.statNum("red")}>{wrong.length}</div>
                <div style={styles.statLbl}>Incorrect</div>
              </div>
              <div style={styles.statBox("neutral")}>
                <div style={styles.statNum("neutral")}>{unanswered}</div>
                <div style={styles.statLbl}>Skipped</div>
              </div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
            <h3 style={{ fontFamily: "Georgia, serif", fontSize: 17, fontWeight: 700, flex: 1 }}>Question Review</h3>
            <button onClick={() => setReviewFilter("wrong")} style={{ padding: "7px 16px", borderRadius: 8, border: "1.5px solid", borderColor: reviewFilter === "wrong" ? "#991b1b" : "#e2e8f0", background: reviewFilter === "wrong" ? "#fee2e2" : "#fff", color: reviewFilter === "wrong" ? "#991b1b" : "#64748b", fontWeight: 600, fontSize: 12, cursor: "pointer" }}>
              Incorrect Only ({wrong.length})
            </button>
            <button onClick={() => setReviewFilter("all")} style={{ padding: "7px 16px", borderRadius: 8, border: "1.5px solid", borderColor: reviewFilter === "all" ? "#0e7490" : "#e2e8f0", background: reviewFilter === "all" ? "#f0f9ff" : "#fff", color: reviewFilter === "all" ? "#0e7490" : "#64748b", fontWeight: 600, fontSize: 12, cursor: "pointer" }}>
              All Questions ({questions.length})
            </button>
          </div>

          {showList.map(({ q, i }) => {
            const isCorrect = answers[i] === q.correct;
            return (
              <div key={i} style={isCorrect ? styles.reviewItemCorrect : styles.reviewItemWrong}>
                <span style={styles.badge(isCorrect ? "correct" : "wrong")}>{isCorrect ? "✓ Correct" : "✗ Incorrect"}</span>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#94a3b8", marginBottom: 6 }}>Q{i + 1}</div>
                <div style={{ fontFamily: "Georgia, serif", fontWeight: 700, fontSize: 15, color: "#0f172a", marginBottom: 12 }}>{q.question}</div>
                {!isCorrect && answers[i] !== undefined && (
                  <div style={{ fontSize: 13, color: "#991b1b", marginBottom: 6 }}>
                    Your answer: <strong>{LABELS[answers[i]]}. {q.options[answers[i]]}</strong>
                  </div>
                )}
                <div style={{ fontSize: 13, color: "#166534", marginBottom: 10 }}>
                  Correct: <strong>{LABELS[q.correct]}. {q.options[q.correct]}</strong>
                </div>
                <div style={{ fontSize: 13, color: "#0c4a6e", background: "#f0f9ff", borderRadius: 8, padding: "10px 12px", borderLeft: "3px solid #0e7490" }}>
                  <strong style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "#0e7490" }}>Rationale: </strong>{q.rationale}
                </div>
              </div>
            );
          })}

          <div style={{ marginTop: 24, textAlign: "center" }}>
            <button style={styles.btn("primary")} onClick={() => setScreen("select")}>← Back to Exam Select</button>
          </div>
        </div>
      </div>
    );
  }

  // ── PRACTICE / EXAM SCREEN ────────────────────────────────────────
  return (
    <div style={styles.root}>
      <div style={styles.header} ref={topRef}>
        <div style={styles.headerInner}>
          <span style={styles.logo}>Vitta Finance Academy</span>
          <div style={styles.progressWrap}>
            <div style={styles.progressBar}>
              <div style={styles.progressFill(pct)} />
            </div>
            <div style={styles.progressText}>
              {answered}/{questions.length} answered
            </div>
          </div>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#0e7490", padding: "4px 10px", background: "#f0f9ff", borderRadius: 8, border: "1px solid #bae6fd", whiteSpace: "nowrap" }}>
            {mode === "practice" ? "Practice" : "Exam"} Mode
          </span>
        </div>
      </div>

      <div style={styles.main}>
        {/* Navigator */}
        <div style={styles.navWrap}>
          {questions.map((_, i) => (
            <button key={i} style={styles.navDot(getNavState(i))} onClick={() => goTo(i)}>
              {i + 1}
            </button>
          ))}
        </div>

        {/* Question Card */}
        <div style={styles.qCard}>
          <div style={styles.qNum}>Question {current + 1} of {questions.length}</div>
          <div style={styles.qText}>{q.question}</div>

          {q.options.map((opt, idx) => {
            const state = getOptionState(idx);
            return (
              <button key={idx} style={styles.optionBtn(state)} onClick={() => selectAnswer(idx)} disabled={
                (mode === "practice" && answers[current] !== undefined) ||
                (mode === "exam" && examSubmitted)
              }>
                <span style={styles.label(state)}>{LABELS[idx]}</span>
                <span>{opt}</span>
              </button>
            );
          })}

          {(showRationale || (mode === "exam" && examSubmitted && answers[current] !== undefined)) && (
            <div style={styles.rationale}>
              <div style={styles.rationaleTitle}>Rationale</div>
              {q.rationale}
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div style={styles.navBtns}>
          <button style={styles.btn("ghost")} onClick={prev} disabled={current === 0}>
            ← Previous
          </button>
          <div style={{ display: "flex", gap: 10 }}>
            {mode === "exam" && !examSubmitted && answered === questions.length && (
              <button style={styles.btn("danger")} onClick={submitExam}>Submit Exam</button>
            )}
            {mode === "exam" && !examSubmitted && answered < questions.length && (
              <button style={{ ...styles.btn("ghost"), color: "#64748b", fontSize: 12 }} onClick={submitExam}>
                Submit ({answered}/{questions.length})
              </button>
            )}
            {current < questions.length - 1 ? (
              <button style={styles.btn("primary")} onClick={next}>Next →</button>
            ) : (
              mode === "practice" && (
                <button style={styles.btn("primary")} onClick={() => {
                  setExamSubmitted(true);
                  setScreen("results");
                  scrollTop();
                }}>View Results</button>
              )
            )}
          </div>
        </div>

        {/* Exit button */}
        <div style={{ marginTop: 24, textAlign: "center" }}>
          <button style={{ ...styles.btn("ghost"), fontSize: 12 }} onClick={() => setScreen("select")}>
            ← Exit to Menu
          </button>
        </div>
      </div>
    </div>
  );
}
